import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from '../../environments/environment';
import { Observable, of } from 'rxjs';
import { map, tap, catchError, finalize } from 'rxjs/operators';

export interface UserEventHistoryItem {
  action: string;
  path: string;
  details?: string;
  meta?: {
    method?: string;
    status?: 'STARTED' | 'SUCCESS' | 'FAILED';
    statusCode?: number | null;
    durationMs?: number;
  };
  createdAt: string;
}

@Injectable({
  providedIn: 'root'
})
export class UserEventHistoryService {
  private readonly maxItems = 50;
  private readonly syncEndpoint = '/audit/user-events/bulk/';
  private readonly fetchEndpoint = '/audit/user-events/';
  private readonly syncThreshold = 50;
  private readonly idleSyncMs = 10 * 60 * 1000;
  private isSyncInProgress = false;
  private idleTimer: ReturnType<typeof setTimeout> | null = null;

  constructor(private readonly http: HttpClient) {}

  trackEvent(
    action: string,
    path: string,
    details?: string,
    meta?: UserEventHistoryItem['meta']
  ): void {
    const entry: UserEventHistoryItem = {
      action,
      path,
      details,
      meta,
      createdAt: new Date().toISOString()
    };

    const existing = this.getEventsForCurrentUser();
    const next = [entry, ...existing].slice(0, this.maxItems);
    this.setEventsForCurrentUser(next);
    this.incrementUnsyncedCounter();
    this.restartIdleTimer();
    this.trySyncByThreshold();
  }

  getRecentEvents(): UserEventHistoryItem[] {
    return this.getEventsForCurrentUser();
  }

  syncFromServer(): Observable<UserEventHistoryItem[]> {
    const userId = sessionStorage.getItem('user_id');
    if (!userId) {
      return of(this.getRecentEvents());
    }

    const headers = this.getAuthHeaders();
    return this.http
      .get<any>(`${environment.APIUrl}${this.fetchEndpoint}?user_id=${userId}`, { headers })
      .pipe(
        map((response) => this.normalizeServerEvents(response)),
        tap((serverEvents) => {
          const merged = this.mergeAndTrim(serverEvents, this.getEventsForCurrentUser());
          this.setEventsForCurrentUser(merged);
        }),
        map(() => this.getEventsForCurrentUser()),
        catchError(() => of(this.getEventsForCurrentUser()))
      );
  }

  clearHistory(): void {
    localStorage.removeItem(this.getStorageKey());
    localStorage.removeItem(this.getUnsyncedCounterKey());
    this.clearIdleTimer();
  }

  forceSyncNow(): Observable<boolean> {
    return this.syncSnapshotToBackend(true);
  }

  private getEventsForCurrentUser(): UserEventHistoryItem[] {
    const raw = localStorage.getItem(this.getStorageKey());
    if (!raw) {
      return [];
    }

    try {
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }

  private setEventsForCurrentUser(events: UserEventHistoryItem[]): void {
    localStorage.setItem(this.getStorageKey(), JSON.stringify(events));
  }

  private trySyncByThreshold(): void {
    if (this.getUnsyncedCounter() < this.syncThreshold) {
      return;
    }

    this.syncSnapshotToBackend().subscribe();
  }

  private syncSnapshotToBackend(force = false): Observable<boolean> {
    if (this.isSyncInProgress) {
      return of(false);
    }

    const userId = sessionStorage.getItem('user_id');
    if (!userId) {
      return of(false);
    }

    const unsynced = this.getUnsyncedCounter();
    if (!force && unsynced <= 0) {
      return of(true);
    }

    const events = this.getEventsForCurrentUser().slice(0, this.maxItems);
    if (events.length === 0) {
      return of(true);
    }

    this.isSyncInProgress = true;
    const headers = this.getAuthHeaders();

    return this.http.post(`${environment.APIUrl}${this.syncEndpoint}`, {
      user_id: userId,
      events
    }, { headers }).pipe(
      tap(() => this.setUnsyncedCounter(0)),
      map(() => true),
      catchError(() => of(false)),
      finalize(() => {
        this.isSyncInProgress = false;
      })
    );
  }

  private restartIdleTimer(): void {
    this.clearIdleTimer();
    this.idleTimer = setTimeout(() => {
      if (this.getUnsyncedCounter() > 0) {
        this.syncSnapshotToBackend().subscribe();
      }
    }, this.idleSyncMs);
  }

  private clearIdleTimer(): void {
    if (!this.idleTimer) {
      return;
    }
    clearTimeout(this.idleTimer);
    this.idleTimer = null;
  }

  private incrementUnsyncedCounter(): void {
    const current = this.getUnsyncedCounter();
    this.setUnsyncedCounter(current + 1);
  }

  private getUnsyncedCounter(): number {
    const raw = localStorage.getItem(this.getUnsyncedCounterKey());
    const parsed = Number(raw || 0);
    return Number.isFinite(parsed) ? parsed : 0;
  }

  private setUnsyncedCounter(value: number): void {
    localStorage.setItem(this.getUnsyncedCounterKey(), String(Math.max(0, value)));
  }

  private getAuthHeaders(): HttpHeaders {
    return new HttpHeaders({
      Authorization: `Bearer ${sessionStorage.getItem('access') || ''}`,
      'Authorized-By': `${sessionStorage.getItem('user_id') || ''}`,
      'X-Skip-Event-History': 'true'
    });
  }

  private normalizeServerEvents(response: any): UserEventHistoryItem[] {
    if (Array.isArray(response)) {
      return response as UserEventHistoryItem[];
    }
    if (Array.isArray(response?.results)) {
      return response.results as UserEventHistoryItem[];
    }
    if (Array.isArray(response?.events)) {
      return response.events as UserEventHistoryItem[];
    }
    return [];
  }

  private mergeAndTrim(
    primary: UserEventHistoryItem[],
    secondary: UserEventHistoryItem[]
  ): UserEventHistoryItem[] {
    const map = new Map<string, UserEventHistoryItem>();
    [...primary, ...secondary].forEach((event) => {
      const key = `${event.createdAt}|${event.action}|${event.path}|${event.details || ''}`;
      map.set(key, event);
    });

    return [...map.values()]
      .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
      .slice(0, this.maxItems);
  }

  private getStorageKey(): string {
    const userId = sessionStorage.getItem('user_id') || 'guest';
    return `user_event_history_${userId}`;
  }

  private getUnsyncedCounterKey(): string {
    const userId = sessionStorage.getItem('user_id') || 'guest';
    return `user_event_history_unsynced_count_${userId}`;
  }
}
